import { createRoot, type Root } from "react-dom/client";
import RightTimeApi from "@/API";
import TestAnswerReplayDocument, { type TestAnswerReplayPayload } from "@/elements/TestAnswerReplayDocument";
import type { TestAnswerPdfDetails } from "@/utils/testAnswerPdfDetails";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

const REPLAY_RENDER_WIDTH_PX = 1280;

function collectStylesForPdf(): { inlineStyles: string[]; stylesheetHrefs: string[] } {
    const inlineStyles: string[] = [];

    for (const tag of document.querySelectorAll("style")) {
        const text = tag.textContent?.trim();
        if (text) inlineStyles.push(text);
    }

    const stylesheetHrefs: string[] = [];
    for (const link of document.querySelectorAll('link[rel="stylesheet"]')) {
        const href = link.getAttribute("href");
        if (href?.trim()) stylesheetHrefs.push(href.trim());
    }

    return { inlineStyles, stylesheetHrefs };
}

function waitForReplayLayout(): Promise<void> {
    return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.setTimeout(resolve, 750);
            });
        });
    });
}

export async function generateTestAnswerPdfAsync(
    testInstanceId: number,
    details?: TestAnswerPdfDetails,
    downloadFilename?: string,
): Promise<void> {
    const replayResp = await RightTimeApi.getAsync(`/TestInstance/${testInstanceId}/AnswerReplay`);
    if (!replayResp?.ok || !replayResp.jsonData) {
        throw new Error("Unable to load answer replay data.");
    }

    const payload = replayResp.jsonData as TestAnswerReplayPayload;
    const container = document.createElement("div");
    container.className = "testAnswerReplayOffscreen";
    container.style.width = `${REPLAY_RENDER_WIDTH_PX}px`;
    document.body.appendChild(container);

    let root: Root | null = null;
    try {
        root = createRoot(container);
        await new Promise<void>((resolve) => {
            root!.render(
                <TestAnswerReplayDocument
                    payload={payload}
                    details={details}
                    onReady={resolve}
                />,
            );
        });
        await waitForReplayLayout();

        const replayRoot = container.querySelector(".testAnswerReplayRoot");
        if (!replayRoot) {
            throw new Error("Answer replay document failed to render.");
        }

        const { inlineStyles, stylesheetHrefs } = collectStylesForPdf();
        const pdfResp = await RightTimeApi.postAsync(
            `/TestInstance/${testInstanceId}/AnswersPdf`,
            {
                html: replayRoot.outerHTML,
                styles: inlineStyles,
                stylesheets: stylesheetHrefs,
                baseUrl: `${window.location.origin}/`,
            },
        );

        if (!pdfResp?.ok) {
            throw new Error("Unable to generate answer PDF.");
        }

        const ok = await downloadResponseAsFile(
            pdfResp,
            downloadFilename?.trim() || `answers_${testInstanceId}.pdf`,
        );
        if (!ok) {
            throw new Error("Unable to generate answer PDF.");
        }
    } finally {
        root?.unmount();
        container.remove();
    }
}
